import type { GeneratedLocation, District, BuildingType } from '@bid/shared-types';

export interface LocationGenerationOptions {
  district?: District;
  buildingType?: BuildingType;
  factionTurf?: string;
}

export const generateLocation = (options: LocationGenerationOptions = {}): GeneratedLocation => {
  const district = options.district || generateRandomDistrict();
  const buildingType = options.buildingType || generateRandomBuildingType();

  return {
    name: generateLocationName(buildingType),
    district,
    buildingType,
    haunted: Math.random() > 0.8,
    factionTurf: options.factionTurf || generateFactionTurf(district),
    securityLevel: Math.floor(Math.random() * 4) + 1,
    description: generateDescription(district, buildingType),
  };
};

const generateRandomDistrict = (): District => {
  const districts: District[] = [
    'Charhollow',
    'Coalridge',
    "Crow's Foot",
    'The Docks',
    'Dunslough',
    'Nightmarket',
    'Six Towers',
    'Silkshore',
    'Whitecrown',
  ];
  return districts[Math.floor(Math.random() * districts.length)];
};

const generateRandomBuildingType = (): BuildingType => {
  const types: BuildingType[] = ['Tavern', 'Factory', 'Den', 'Vault', 'Shop', 'Workshop', 'Church'];
  return types[Math.floor(Math.random() * types.length)];
};

const generateLocationName = (buildingType: BuildingType): string => {
  const adjectives = ['The Rusted', 'The Golden', 'The Hidden', 'The Broken', 'The Silent'];
  const nouns: Record<BuildingType, string[]> = {
    Tavern: ['Anchor', 'Harp', 'Crow', 'Dice', 'Flagon'],
    Factory: ['Works', 'Mill', 'Forge', 'Works', 'Foundry'],
    Den: ['Hole', 'Cellar', 'Hideout', 'Nest', 'Lair'],
    Vault: ['Safe', 'Depository', 'Treasury', 'Chamber', 'Lock'],
    Shop: ['Emporium', 'Market', 'Bazaar', 'Store', 'Shoppe'],
    Workshop: ['Studio', 'Lab', 'Workbench', 'Atelier', 'Crafts'],
    Church: ['Temple', 'Shrine', 'Sanctuary', 'Chapel', 'Hall'],
  };

  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[buildingType][Math.floor(Math.random() * nouns[buildingType].length)];

  return `${adj} ${noun}`;
};

const generateFactionTurf = (district: District): string => {
  const factionTurfs: Record<string, string[]> = {
    "Crow's Foot": ['The Lampblacks', 'The Red Sashes', 'The Crows'],
    Charhollow: ['The Gray Cloaks', 'The Billhooks'],
    'Six Towers': ['The Hive', 'The Unseen'],
    'The Docks': ['The Lampblacks', 'The Gray Cloaks'],
    Coalridge: ['The Billhooks', 'The Crows'],
  };

  const factions = factionTurfs[district] || ['Independent'];
  return factions[Math.floor(Math.random() * factions.length)];
};

const generateDescription = (district: District, buildingType: BuildingType): string => {
  const districtDescriptions: Record<District, string> = {
    Charhollow: 'A crowded, working-class district',
    Coalridge: 'An industrial area with factories and smokestacks',
    "Crow's Foot": 'A rough, gang-controlled neighborhood',
    'The Docks': 'A busy port area with warehouses and ships',
    Dunslough: 'A poor district by the river',
    Nightmarket: 'A commercial district open all night',
    'Six Towers': 'An affluent neighborhood with grand spires',
    Silkshore: 'A district of vice and entertainment',
    Whitecrown: 'The wealthy district with government buildings',
  };

  return `A ${buildingType.toLowerCase()} in ${district}, ${districtDescriptions[district].toLowerCase()}.`;
};
